import { DeliverySpeedDetail } from "@swiggy-private/cart-types";
import { getBridgeSdk, JSON_ENCODED_HEADERS_LIST } from "@swiggy-private/checkout-core";
import { GlobalHeaderState, SwgyHeaderType, SwgyHeadersList, SwgyHeadersKeys } from "@swiggy-private/checkout-core";
import { safeJsonParse } from "@swiggy-private/core-ui";
import { escapeHTML } from "@swiggy-private/core-utils";

type Dict<T> = { [header: string]: T | undefined };

/**
 * Parses a JSON-encoded header value (e.g. `foodcartheaders`) and merges its inner
 * keys (e.g. `x-cart-type`) into the flat header map. Mirrors the flattening done on
 * the boot paths (server `parseJSONEncodedHeaders` / `getSafeHeaders` and the
 * `getUpdatedHeaders` inline scripts) so that re-syncing headers from the bridge
 * does not drop the spread-out keys.
 */
const spreadJSONEncodedHeader = (swgyHeaders: Dict<string>, value: string): void => {
    try {
        const parsedValue = safeJsonParse<Record<string, string> | null>(value, null);
        if (!parsedValue) {
            return;
        }
        for (const key in parsedValue) {
            swgyHeaders[key.toLowerCase()] = String(escapeHTML(parsedValue[key] || ""));
        }
    } catch (error) {
        console.error("Error parsing JSON encoded value in spreadJSONEncodedHeader", error);
    }
};

export const syncClientHeaders = async (): Promise<void> => {
    return getBridgeSdk()
        .getSwiggyHeadersDataFromClient()
        .then((headers) => {
            if (!headers) {
                return;
            }

            const swgyHeaders: Dict<string> = {};
            for (const property in headers) {
                const value = headers[property];
                if (
                    typeof value === "string" &&
                    SwgyHeadersList.indexOf(property.toLowerCase() as SwgyHeadersKeys) !== -1
                ) {
                    if (JSON_ENCODED_HEADERS_LIST.indexOf(property.toLowerCase()) !== -1) {
                        spreadJSONEncodedHeader(swgyHeaders, value);
                    } else {
                        swgyHeaders[property.toLowerCase()] = value;
                    }
                }
            }

            GlobalHeaderState.init(swgyHeaders as SwgyHeaderType);
            return;
        });
};

export const syncCartAddressHeaders = ({
    cartId,
    addressId,
}: {
    cartId: string | number;
    addressId: string | number;
}): void => {
    GlobalHeaderState.updateHeaders({
        cartaddressid: String(addressId),
        cartid: String(cartId),
    });
};

export const getSelectedSLA = (slaOptions: DeliverySpeedDetail[], selectedType: string): number | null => {
    const selectedOption = slaOptions.find((option) => option.type === selectedType);
    return selectedOption ? selectedOption.sla : null;
};
